import { useEffect, useRef } from "react";
import { useListContext } from "ra-core";

// Scroll-position restoration for the NON-virtualized ScrollArea lists — Action
// Items, Zoom Meetings, Sales. (The virtualized lists, Contacts + Companies, use
// the richer react-virtuoso snapshot in VirtualizedListContent instead.)
//
// These lists render all their rows inside a themed <ScrollArea>, so restoring
// is just saving and re-applying the viewport's scrollTop — no virtualizer state
// to reconstruct. Snapshots are keyed by list identity (resource + filters +
// sort) and held OUTSIDE the component so they survive the unmount when a detail
// page opens; returning re-mounts the list and reapplies its offset. In-memory
// only: cleared on a hard reload, matching the react-query page cache whose
// retained rows give the restored offset real content to land on.
const scrollTops = new Map<string, number>();

/**
 * Returns a `viewportRef` to pass to `<ScrollArea viewportRef={...}>`. Saves the
 * scroll position as the user scrolls, and restores it once — after the list has
 * data, so the offset lands on a row rather than clamping to 0 against an
 * empty/short container.
 */
export const useScrollAreaRestoration = () => {
  const { resource, filterValues, sort, data, isPending } = useListContext();
  const key = JSON.stringify({ resource, filterValues, sort });
  const keyRef = useRef(key);
  keyRef.current = key;
  const viewportRef = useRef<HTMLDivElement>(null);

  // Save continuously while scrolling.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const onScroll = () => scrollTops.set(keyRef.current, viewport.scrollTop);
    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", onScroll);
  }, []);

  // Restore exactly once, when the list first has data — mirrors the
  // mount-only restore of the virtualized lists (a mid-life filter change is a
  // new view, not a restore).
  const ready = !isPending && Array.isArray(data) && data.length > 0;
  const hasRestored = useRef(false);
  useEffect(() => {
    if (!ready || hasRestored.current) return;
    hasRestored.current = true;
    const viewport = viewportRef.current;
    const saved = scrollTops.get(keyRef.current);
    if (viewport && saved != null) {
      requestAnimationFrame(() => {
        viewport.scrollTop = saved;
      });
    }
  }, [ready]);

  return viewportRef;
};
